<template>
  <EditionModal :is-displayed="id !== null" :query-param="infoCreationQueryParam" class="info-creation-modal">
    <div v-if="id !== null" class="info-creation-modal__wrapper">
      <div class="info-creation-modal__content-wrapper">
        <div class="info-creation-modal__cols-wrapper">
          <div class="info-creation-modal__details">
            <div class="legend legend--bottom-margin">
              {{ $t('message.DESCRIPTION') }}
            </div>
            <EditableLabel
              ref="description"
              :key="infoObj ? infoObj.id : -1"
              v-model="textValue"
              class="info-creation-modal__text"
              :placeholder="$t('message.INFORMATION')"
              :disabled="noEdition"
              use-tiptap-editor
              display-tiptap-menu
            />

            <div v-if="noEdition" class="field-hint info-creation-modal__no-edition-hint">
              {{ noEditionReason }}
            </div>

            <div class="info-creation-modal__published">
              <div class="legend legend--bottom-margin">
                {{ $t('message.INFO_RELEASE_DATE') }}
              </div>
              <DateField
                v-model="infoObj.published"
                :placeholder="$t('message.INFO_RELEASE_DATE')"
                :disabled="noEdition"
                @input="editInfo('published')"
              />
            </div>

            <div class="info-creation-modal__unpublished">
              <div class="legend legend--bottom-margin">
                {{ $t('message.INFO_REMOVAL_DATE') }}
              </div>
              <DateField
                v-model="infoObj.unpublished"
                :placeholder="$t('message.INFO_REMOVAL_DATE')"
                :disabled="noEdition"
                @input="editInfo('unpublished')"
              />
            </div>

            <AdvancedTeamPicker
              v-model="infoObj.teams"
              class="info-creation-modal__teams"
              :legend="$t('message.OTHER_TEAMS')"
              :disable-inaccessible="currentTeam.allowSendingInfoAnyTeam !== 1"
              :disabled="noEdition"
              ok-button
              display-all-teams
              use-editable-label
              @input="editInfo('teams')"
            />

          </div>

          <div class="info-creation-modal__files-wrapper">
            <div class="legend legend--bottom-margin">
              {{ $t('message.FILES') }}
            </div>
            <FileChipList
              v-model="infoObj.files"
              :editable="!noEdition"
              column
              upload-zone
              pathname="informations/"
              own-file-viewer
              display-files
              class="info-creation-modal__files"
              @input="editInfo('files')"
            />

            <div v-if="relatedGw">
              <div class="legend legend--bottom-margin">
                {{ $t('message.INFO_RELATED_GW') }}
              </div>

              <RouterLink
                :to="{ path: '/gw-rating', query: { gwId: relatedGw.id } }"
                class="
                  generic-border
                  generic-border--text-btn
                  generic-border--gray-border
                  info-creation-modal__gw-rating
                "
              >
                <Icon>{{ editMedium }}</Icon>
                <span>{{ $t('message.GW_RATE_REVIEW') }}</span>
              </RouterLink>
              <br>
              <RouterLink
                :to="`/gw-completed/${relatedGw.id}`"
                class="
                  generic-border
                  generic-border--text-btn
                  generic-border--gray-border
                  info-creation-modal__gw-link
                "
              >
                <Icon>{{ link }}</Icon>
                <span>{{ $t('message.GW_PREVIEW_TOUR') }}</span>
              </RouterLink>
            </div>

          </div>
        </div>
      </div>
      <div class="info-creation-modal__buttons">
        <button
          class="generic-border generic-border--text-btn generic-border--clickable generic-border--red"
          :class="{ 'greyed-out': noEdition }"
          :disabled="noEdition"
          @click="confirmDeleteInfo"
        >
          <Icon>{{ deleteMedium }}</Icon>
          {{ $t("message.DELETE") }}
        </button>
        <button
          class="generic-border generic-border--text-btn generic-border--clickable generic-border--blue"
          :class="{ 'greyed-out': noEdition }"
          @click="saveAndQuit"
        >
          <Icon>{{ pageMedium }}</Icon>
          {{ $t("message.SAVE") }}
        </button>
      </div>
    </div>
  </EditionModal>
</template>

<script>
/**
 * Information edition/creation modal
 *
 */

import { cloneDeep } from 'lodash';
// Vuex
import { createNamespacedHelpers, mapState, mapActions } from 'vuex';
import toastr from 'toastr';
import { FETCH_TEAMS, FETCH_INFOS, EDIT_INFO, DELETE_INFO } from '@/store/action-types';
import { GET_TEAMS } from '@/store/mutation-types';
// Import components
import AdvancedTeamPicker from '@/components/advanced-team-picker.vue';
import DateField from '@/components/date-field.vue';
import EditableLabel from '@/components/editable-label.vue';
import EditionModal from '@/components/edition-modals/edition-modal.vue';
import FileChipList from '@/components/file-system/file-chip-list.vue';
import Icon from '@/components/icon.vue';
// Import socket handling utils
import mixinSocketHandler from '@/mixins/socket-handler.vue';
import SocketMessages from '@/utils/classes/SocketMessages';
// Import utils
import { calert, decodeHTML } from '@/utils/main';
import { globalEventDispatcher, ShizenGlobalEvent } from '@/utils/event-dispatcher/global';
import { infoCreationQueryParam } from '@/utils/navigation-constants';
import { getGWInfoSummary } from '@/utils/gw-utils';
import { deleteMedium, editMedium, link, pageMedium } from '@/assets/icons/';


const { mapState: fileMapState } = createNamespacedHelpers('file');

export default {

  name: 'InfoCreationModal',

  components: {
    AdvancedTeamPicker,
    DateField,
    EditableLabel,
    EditionModal,
    FileChipList,
    Icon,
  },

  mixins: [mixinSocketHandler],

  data() {
    return {
      infoCreationQueryParam,
      deleteMedium,
      editMedium,
      link,
      pageMedium,
      // Before design update
      saving: false,
      // Info data
      emptyInfo: {},
      infoObj: {},
      // Other data
      promptBeforeLeave: false,
      infoDeleted: false,
      // Properties used by other components. See src/views/readme.md
      pageBackButtonAction: () => {
        this.quit(this.closeModal);
      },
    };
  },

  computed: {
    /** Number: ID of the information. If 0, creating a new information */
    id() {
      return this.$route.query[infoCreationQueryParam] === undefined
        ? null
        : Number(this.$route.query[infoCreationQueryParam]);
    },
    /** Boolean: Whether the info creation was started because the user was following a meeting */
    followingMeeting() {
      return this.$route.query.followingMeeting && Number(this.$route.query.followingMeeting) === 1;
    },
    /** Boolean: Whether the team that created the info is the current team */
    isCurrentTeam() {
      return this.infoObj && Number(this.infoObj.publisher) === this.currentTeam.id;
    },
    /** Boolean: Whether the info should be editable */
    noEdition() {
      return !this.isCurrentTeam || !!this.relatedGw;
    },
    /** String: Hint that indicates why an information might not be editable */
    noEditionReason() {
      if (this.relatedGw) {
        return this.$t('message.INFO_DISABLED_HINT_GW');
      }
      if (!this.isCurrentTeam) {
        return this.$t('message.INFO_DISABLED_HINT_OTHER_TEAM');
      }
      return '';
    },
    /** Object: HTML classes applied to fields */
    fieldsClasses() {
      return {
        'clickable-hoverable': !this.noEdition,
      };
    },
    /** Object: Information object as found in state */
    stateInfo() {
      return this.infoPerId[`${this.id}`];
    },
    /** String: Header title of the modal */
    infoHeaderTitle() {
      return this.id === null || this.id === 0 || this.promptBeforeLeave
        ? this.$t('message.NEW_INFO')
        : this.$t('message.INFORMATION');
    },
    // =================
    // Related GW if any
    relatedGw() {
      return this.infoObj ? this.$store.state.gembaWalks.gwPerId[`${this.infoObj.gwToRate || 0}`] : {};
    },
    textValue: {
      get() {
        return this.relatedGw
          ? getGWInfoSummary(this.relatedGw, this.currentTeam, this.infoObj.text)
          : this.infoObj.text;
      },
      set(infoText) {
        this.infoObj.text = infoText;
        this.editInfo('text');
      },
    },
    /** Array: All GW checks related to this team */
    ...fileMapState({
      lastFileDeleted: state => state.lastFileDeleted,
    }),
    ...mapState({
      teams: state => state[GET_TEAMS.stateKey] || [],
      teamsLoading: state => state[GET_TEAMS.loadingKey],
      infoPerId: state => state.infoPerId,
    }),
    // Properties used by other components. See src/views/readme.md
    pageTitle() {
      return this.$t('message.NEW_INFO');
    },
  },

  watch: {
    lastFileDeleted(file) {
      if (this.infoObj.files.indexOf(file.id) !== -1) {
        this.refresh();
      }
    },
    id(newVal, oldVal) {
      if (this.id !== null) {
        // Id changes. The page must be refreshed and a new info must be created if id === 0
        this.init();
        // If the old id was 0, this means that the information currently displayed is a new one
        this.promptBeforeLeave = oldVal === 0;
      }
    },
    isInfoCreationDisplayed() {
      if (!this.isInfoCreationDisplayed) {
        // If the information creation gets hidden, reset parameters
        this.infoDeleted = false;
      }
    },
    stateInfo() {
      this.infoObj = cloneDeep(this.stateInfo);
    },
  },

  created() {
    if (this.id !== null) {
      this.init();
      this.promptBeforeLeave = this.id === 0;
    }

    // Initialize empty info
    this.emptyInfo = {
      message: '',
      published: this.$moment().format('YYYY-MM-DD'),
      unpublished: this.$moment().add(1, 'day').format('YYYY-MM-DD'),
      teams: [this.currentTeam.id],
      files: [],
      selectCurrentTeam: true,
      selectManagedTeams: false,
    };
    this.infoObj = Object.assign({}, this.emptyInfo);


    this.handleSocketMessage(SocketMessages.CC_MSGS.INFO_EDITION, (msg) => {
      if (Number(msg.id) !== Number(this.infoObj.id)) return;
      this.refresh();
    });

    if (!this.teamsLoading) {
      this.$store.dispatch(FETCH_TEAMS);
    }

    // Register the event from the router triggered when the user created a new info while another one is displayed.
    // If the modal is in the process of creating a new info,
    // the user will be prompted to keep/delete the new info or to cancel the navigation
    globalEventDispatcher.register(ShizenGlobalEvent.NEW_MODAL_WHILE_ONE_ACTIVE, (params) => {
      if (this.id !== null) {
        // Handle the event if the modal is currently displayed
        this.quit(params.next);
      }
    });
  },

  methods: {
    decodeHTML,
    /** Close the info edition modal */
    closeModal() {
      this.$router.back();
    },
    /** Try to leave the page */
    quit(next) {
      if (this.promptBeforeLeave) {
        calert(this.$t('message.INFO_SAVE_BEFORE_LEAVE'), [
          {
            title: this.$t('message.YES_LEAVE_SAVE'),
            action: () => {
              this.saveAndQuit();
              if (next) {
                next();
              }
            },
            sClass: 'green',
          }, {
            title: this.$t('message.NO_LEAVE_WITHOUT_SAVING'),
            action: () => {
              this.promptBeforeLeave = false;
              this.deleteInfo(next);
            },
            sClass: 'red',
          }, {
            title: this.$t('message.CANCEL_NOT_LEAVING'),
          },
        ]);
      } else {
        next();
      }
    },
    /** If the info exists, fetch it. Otherwise, create a new one */
    init() {
      if (!this.followingMeeting && Number(this.id) === 0) {
        this.infoObj = Object.assign({}, this.emptyInfo);
        this.editInfo(['published', 'unpublished', 'teams'], {}, (data) => {
          if (data && data.newId) {
            const query = Object.assign({}, this.$route.query);
            query[infoCreationQueryParam] = data.newId;
            this.$router.replace({ query });
          }
        });
      } else {
        this.refresh();
        if (this.followingMeeting) {
          const query = { ...Object.assign({}, this.$route.query), followingMeeting: undefined };
          this.$router.replace({ query });
        }
      }
    },
    /** Fetch the information */
    refresh() {
      this.saving = false;
      this.FETCH_INFOS({ id: this.id });
    },
    /** Save the information's edited field */
    editInfo(fields, additionalParams = {}, callback = null) {
      if (this.infoDeleted) {
        return;
      }
      const params = { ...additionalParams, id: this.infoObj.id };
      fields = fields instanceof Array ? fields : [fields];
      fields.forEach((key) => {
        params[key] = this.infoObj[key];
      });
      this.EDIT_INFO(params).then((data) => {
        if (data instanceof Object) {
          toastr.remove();
          toastr.success(this.$t('message.INFO_SAVED'));
          if (callback instanceof Function) {
            callback(data);
          }
          this.sendSocketUpdate();
          this.sendSocketMessage(SocketMessages.CC_MSGS.MEETING_INFOS);
        } else {
          toastr.error(this.$t('message.CONNECTION_ERROR'));
        }
      });
    },
    /** Clicked 'save' btn */
    saveAndQuit(next) {
      if (this.promptBeforeLeave) {
        const params = { id: this.infoObj.id, submit: 1 };
        this.EDIT_INFO(params).then((data) => {
          if (data instanceof Object) {
            this.promptBeforeLeave = false;
            if (next instanceof Function) {
              next();
            } else {
              globalEventDispatcher.dispatch(ShizenGlobalEvent.INFORMATION_CREATED);
              this.closeModal();
            }
          } else {
            toastr.error(this.$t('message.CONNECTION_ERROR'));
          }
        });
      } else if (next instanceof Function) {
        next();
      } else {
        this.closeModal();
      }
    },
    /** Deletes the action. If the action was sent from another team, return it */
    deleteInfo(next) {
      this.DELETE_INFO({ id: this.infoObj.id }).then((data) => {
        if (data) {
          toastr.success(this.$t('message.INFO_DELETED'));
          this.infoDeleted = true;
          this.promptBeforeLeave = false;
          this.sendSocketMessage(SocketMessages.CC_MSGS.MEETING_INFOS);
          if (next) {
            next();
          }
        } else {
          toastr.error(this.$t('message.CONNECTION_ERROR'));
        }
      });
    },
    /** Delete button clicked, need confirmation */
    confirmDeleteInfo() {
      calert(this.$t('message.INFO_DELETE_CONFIRM'), [
        {
          title: this.$t('message.INFO_DELETE'),
          action: () => {
            this.deleteInfo(this.closeModal);
          },
          sClass: 'red',
        },
        { title: this.$t('message.CANCEL') },
      ]);
    },
    /** Send content change messages */
    sendSocketUpdate() {
      this.sendSocketMessage({ socketCompany: 1, id: this.infoObj.id, msg: SocketMessages.CC_MSGS.INFO_EDITION });
    },
    ...mapActions({
      FETCH_INFOS,
      EDIT_INFO,
      DELETE_INFO,
    }),
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.info-creation-modal {

  &__wrapper {
    display: flex;
    flex-flow: column;
    height: 100%;

  }

  &__content-wrapper {
    padding: $page-content-padding;
    box-sizing: border-box;
    flex: 1;
    flex-flow: column;
    height: calc(100% - #{$buttons-zone-height});
  }

  &__cols-wrapper {
    background: white;
    padding: 1rem;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    border-radius: $main-border-radius;
    box-sizing: border-box;
    overflow: auto;
    max-height: 100%;

    > * {
      flex: 0 0 calc(50% - 3rem);
      box-sizing: border-box;
      min-width: 0px;
    }
  }

  &__buttons {
    background: #fff;
    height: $buttons-zone-height;
    box-sizing: border-box;
    padding: $buttons-zone-padding;
    display: flex;
    justify-content: space-between;
    align-items: center;

    > * {
      font-weight: 600;
      justify-content: center;
    }

    &__right > * {
      margin-left: 1rem;
    }
  }

  & &__text {
    padding: 0px;
    width: 100%;
    margin-bottom: 1.5rem;
  }

  &__no-edition-hint {
    margin-top: .3rem;
  }

  &__sep.sep {
    margin: 1.3rem -2.86em;
    width: calc(100% + 5.72rem)
  }

  &__published, &__unpublished, &__teams, &__files {
    margin-bottom: 1.5rem;
  }

  &__teams {
    .editable-label-text {
      border: 1px solid $color-gray-border;
      box-sizing: border-box;
      border-radius: $high-border-radius;
      resize: none;
      padding: .7rem .8rem;
    }
  }

  &__published, &__unpublished {
    .date-field {
      width: 100%;
    }
  }

  & &__gw-rating, & &__gw-link {
    display: inline-flex;

    &:last-child {
      margin-top: 1rem;
    }
  }

  &__files-wrapper {
    margin-top: 0.3rem;
  }
}

@media (max-width: $smartphone-portrait) {
  .info-creation-modal {

    &__cols-wrapper {
      flex-flow: column;

      > * {
        flex: auto;
        width: 100%;
      }
    }
  }
}
</style>
